import React, { useState, useEffect, useCallback, useRef } from "react";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const fmtTs = (ts) => {
  if (!ts) return "—";
  const n = Number(ts);
  if (!n) return "—";
  return new Date(n * 1000).toLocaleString();
};

const money = (v, cur) => (v || v === 0 ? `${cur && cur !== "USD" ? cur + " " : "$"}${Number(v).toFixed(2)}` : "—");

// TikTok Shop order statuses → display color
const STATUS_COLORS = {
  UNPAID: "rgba(10,10,15,0.58)",
  AWAITING_SHIPMENT: "#0033FF",
  AWAITING_COLLECTION: "#0033FF",
  PARTIALLY_SHIPPING: "#f59e0b",
  IN_TRANSIT: "#60a5fa",
  DELIVERED: "#34d399",
  COMPLETED: "#34d399",
  CANCELLED: "#ef4444",
};
const fmtStatus = (s) => (
  <span style={{ ...S.typeBadge, color: STATUS_COLORS[s] || "#0A0A0F", fontSize: 12 }}>
    {(s || "—").replace(/_/g, " ")}
  </span>
);
// A TikTok order is shippable (can pull a label) while awaiting shipment.
const isShippable = (s) => s === "AWAITING_SHIPMENT" || s === "AWAITING_COLLECTION" || s === "PARTIALLY_SHIPPING";

export default function TikTokPage({ store }) {
  const storeKey = store === "vibratovault" ? "vibratovault" : "designshop";
  const storeLabel = storeKey === "vibratovault" ? "Vibrato Vault" : "Design Shop";

  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [listings, setListings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [labeling, setLabeling] = useState("");
  const [edits, setEdits] = useState({}); // skuId -> { price, stock }
  const [savingSku, setSavingSku] = useState("");

  const [settings, setSettings] = useState({
    connected: false, appConfigured: false, shopId: "", shopName: "",
    region: "", lastSynced: "", tokenExpiresAt: "",
  });

  // CEO-only app credential form
  const [cred, setCred] = useState({ empId: "", password: "", appKey: "", appSecret: "" });
  const [credSaving, setCredSaving] = useState(false);
  const [showCredForm, setShowCredForm] = useState(false);
  const connectPoll = useRef(null);

  const fetchSettings = useCallback(() => {
    fetch(`${API_URL}/api/tiktok/settings?store=${storeKey}`)
      .then((r) => r.json())
      .then((s) => setSettings((prev) => ({ ...prev, ...s })))
      .catch(() => {});
  }, [storeKey]);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/tiktok/orders?store=${storeKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else if (data.orders) setOrders(data.orders);
        setLoading(false);
      })
      .catch((err) => { setError(`Fetch failed: ${err.message}`); setLoading(false); });
  }, [storeKey]);

  const fetchListings = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/tiktok/listings?store=${storeKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else if (data.listings) setListings(data.listings);
        setLoading(false);
      })
      .catch((err) => { setError(`Fetch failed: ${err.message}`); setLoading(false); });
  }, [storeKey]);

  const fetchPayouts = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/tiktok/payouts?store=${storeKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else if (data.statements) setPayouts(data.statements);
        setLoading(false);
      })
      .catch((err) => { setError(`Fetch failed: ${err.message}`); setLoading(false); });
  }, [storeKey]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => {
    if (tab === "orders") fetchOrders();
    if (tab === "listings" && settings.connected) fetchListings();
    if (tab === "payouts" && settings.connected) fetchPayouts();
  }, [tab, settings.connected, fetchOrders, fetchListings, fetchPayouts]);

  // Auto-dismiss the success toast
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Surface the OAuth callback result if the user landed back here with a param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tiktok_connected")) {
      setToast("Connected to TikTok Shop ✓");
      fetchSettings();
    } else if (params.get("tiktok_error")) {
      setError(`TikTok connection failed: ${params.get("tiktok_error")}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback auto-sync while the page is open (server poller also runs every 5 min)
  useEffect(() => {
    if (!settings.connected) return undefined;
    const iv = setInterval(() => {
      fetch(`${API_URL}/api/tiktok/sync?store=${storeKey}`, { method: "POST" })
        .then((r) => r.json())
        .then((d) => { if (d && d.synced > 0) { fetchOrders(); fetchSettings(); } })
        .catch(() => {});
    }, 60000);
    return () => clearInterval(iv);
  }, [settings.connected, storeKey, fetchOrders, fetchSettings]);

  // Clean up the connect poller on unmount
  useEffect(() => () => { if (connectPoll.current) clearInterval(connectPoll.current); }, []);

  const connect = async () => {
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/tiktok/oauth/start?store=${storeKey}`);
      const data = await r.json();
      if (data.error) { setError(data.error); return; }
      window.open(data.authorizeUrl, "_blank", "noopener");
      setConnecting(true);
      if (connectPoll.current) clearInterval(connectPoll.current);
      let tries = 0;
      connectPoll.current = setInterval(async () => {
        tries += 1;
        try {
          const s = await fetch(`${API_URL}/api/tiktok/settings?store=${storeKey}`).then((x) => x.json());
          if (s.connected) {
            setSettings((prev) => ({ ...prev, ...s }));
            setConnecting(false);
            setToast("Connected to TikTok Shop ✓");
            clearInterval(connectPoll.current);
          } else if (tries > 90) { // ~3 min
            setConnecting(false);
            clearInterval(connectPoll.current);
          }
        } catch (e) { /* ignore transient errors */ }
      }, 2000);
    } catch (e) { setError(`Could not start TikTok connect: ${e.message}`); }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${storeLabel} from TikTok Shop? Inventory will stop auto-syncing until reconnected.`)) return;
    await fetch(`${API_URL}/api/tiktok/disconnect?store=${storeKey}`, { method: "POST" }).catch(() => {});
    fetchSettings();
    setToast("Disconnected from TikTok Shop");
  };

  const syncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/tiktok/sync?store=${storeKey}`, { method: "POST" });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        setSyncResult(d);
        const errs = (d.errors || []).length;
        setToast(`Synced ${d.synced} • skipped ${d.skipped || 0}${errs ? ` • ${errs} error${errs > 1 ? "s" : ""}` : ""}`);
        fetchOrders();
        fetchSettings();
      }
    } catch (e) { setError(`Sync failed: ${e.message}`); }
    setSyncing(false);
  };

  const getLabel = async (orderId) => {
    setLabeling(orderId);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/tiktok/orders/${orderId}/label?store=${storeKey}`);
      const d = await r.json();
      if (d.error) setError(d.error);
      else if (d.url) window.open(d.url, "_blank", "noopener");
      else setError("No shipping label available for this order yet.");
    } catch (e) { setError(`Label failed: ${e.message}`); }
    setLabeling("");
  };

  const saveSku = async (productId, skuId) => {
    const e = edits[skuId];
    if (!e) return;
    setSavingSku(skuId);
    setError("");
    try {
      const body = { skuId };
      if (e.price !== undefined && e.price !== "") body.price = parseFloat(e.price);
      if (e.stock !== undefined && e.stock !== "") body.stock = parseInt(e.stock, 10);
      const r = await fetch(`${API_URL}/api/tiktok/listings/${productId}?store=${storeKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        setToast("Listing updated on TikTok ✓");
        setEdits((prev) => { const n = { ...prev }; delete n[skuId]; return n; });
        fetchListings();
      }
    } catch (err) { setError(`Update failed: ${err.message}`); }
    setSavingSku("");
  };

  const saveCredentials = async () => {
    setCredSaving(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/tiktok/app-credentials`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cred),
      });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        setToast("TikTok app credentials saved ✓");
        setShowCredForm(false);
        setCred({ empId: "", password: "", appKey: "", appSecret: "" });
        fetchSettings();
      }
    } catch (e) { setError(`Save failed: ${e.message}`); }
    setCredSaving(false);
  };

  const filteredOrders = orders.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (o.title || "").toLowerCase().includes(s)
      || (o.sku || "").toLowerCase().includes(s)
      || (o.buyerName || "").toLowerCase().includes(s)
      || String(o.orderId).includes(s);
  });

  const filteredListings = listings.filter((l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.title || "").toLowerCase().includes(s)
      || (l.skus || []).some((sk) => (sk.sellerSku || "").toLowerCase().includes(s));
  });

  const tabs = [
    { key: "orders", label: "Orders" },
    { key: "listings", label: "Listings" },
    { key: "payouts", label: "Payouts" },
    { key: "connect", label: "Connection" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1 }}>TikTok Shop</h2>
          <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginTop: 2 }}>direct TikTok Shop API · {storeLabel}</div>
        </div>
        {settings.connected && tab !== "connect" && (
          <button onClick={syncNow} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1, cursor: syncing ? "wait" : "pointer" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
            {syncing ? "Syncing…" : "Sync Sales"}
          </button>
        )}
      </div>

      <div style={{ ...S.nav, marginBottom: 20 }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setSearch(""); }} style={{ ...S.navBtn, ...(tab === t.key ? S.navBtnActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {toast && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(52,211,153,0.12)", border: "1px solid #34d399", borderRadius: 0, fontSize: 14, color: "#0c3a2a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{toast}</span>
          <button onClick={() => setToast("")} style={{ background: "none", border: "none", color: "#0c3a2a", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", borderRadius: 0, fontSize: 14, color: "#ef4444", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {syncResult && tab === "orders" && (
        <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid #22c55e", borderRadius: 0, padding: "12px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#34d399", marginBottom: 4 }}>Sync Complete</div>
          <div style={{ fontSize: 14, color: "#0A0A0F" }}>
            {syncResult.synced || 0} synced, {syncResult.skipped || 0} already synced, {(syncResult.errors || []).length} errors
          </div>
          {(syncResult.errors || []).length > 0 && (
            <div style={{ marginTop: 8 }}>
              {syncResult.errors.map((e, i) => (
                <div key={i} style={{ fontSize: 13, color: "#ef4444" }}>
                  Order {e.orderId}: {e.reason} {e.sku ? `(SKU: ${e.sku})` : ""} — {e.title || ""}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Connection Tab ── */}
      {tab === "connect" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>Shop Connection</div>
            {settings.connected ? (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 0, background: "#34d399", display: "inline-block" }} />
                  <span style={{ fontSize: 16, color: "#0A0A0F", fontWeight: 600 }}>
                    Connected{settings.shopName ? ` — ${settings.shopName}` : ""}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 13, color: "rgba(10,10,15,0.58)", marginBottom: 16 }}>
                  {settings.shopId && (<><span>Shop ID</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{settings.shopId}</span></>)}
                  {settings.region && (<><span>Region</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{settings.region}</span></>)}
                  <span>Last synced</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{fmtTs(settings.lastSynced)}</span>
                  <span>Token valid until</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{fmtTs(settings.tokenExpiresAt)}</span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={syncNow} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1 }}>{syncing ? "Syncing…" : "Sync Now"}</button>
                  <button onClick={disconnect} style={{ ...S.clearBtn, fontSize: 13 }}>Disconnect</button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 16, lineHeight: 1.5 }}>
                  {settings.appConfigured
                    ? `Authorize ${storeLabel} on TikTok Shop. Orders will then sync directly and deduct inventory. Only orders placed after you connect are deducted.`
                    : "Add the TikTok Shop app credentials below first (CEO only), then authorize the shop."}
                </p>
                <button onClick={connect} disabled={!settings.appConfigured || connecting} style={{ ...S.addBtn, opacity: (!settings.appConfigured || connecting) ? 0.5 : 1, cursor: (!settings.appConfigured || connecting) ? "not-allowed" : "pointer" }}>
                  {connecting ? "Waiting for TikTok…" : `Connect ${storeLabel} to TikTok Shop`}
                </button>
                {connecting && (
                  <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginTop: 8 }}>Approve access in the TikTok tab that opened, then come back here.</div>
                )}
              </div>
            )}
          </div>

          {/* App credentials (CEO only) */}
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)" }}>TikTok App Credentials</div>
              {settings.appConfigured && !showCredForm && (
                <span style={{ fontSize: 13, color: "#34d399", display: "flex", alignItems: "center", gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                  Configured
                </span>
              )}
            </div>
            <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginBottom: 16, lineHeight: 1.5 }}>
              The App Key + App Secret from your TikTok Shop Partner Center app. Stored server-side, never shown again. CEO credentials required to save.
            </p>
            {(!settings.appConfigured || showCredForm) ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 460 }}>
                <input value={cred.appKey} onChange={(e) => setCred({ ...cred, appKey: e.target.value })} placeholder="App Key" style={S.input} />
                <input value={cred.appSecret} onChange={(e) => setCred({ ...cred, appSecret: e.target.value })} placeholder="App Secret" type="password" style={S.input} />
                <div style={{ display: "flex", gap: 10 }}>
                  <input value={cred.empId} onChange={(e) => setCred({ ...cred, empId: e.target.value })} placeholder="CEO Employee ID" style={{ ...S.input, flex: 1 }} />
                  <input value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })} placeholder="CEO Password" type="password" style={{ ...S.input, flex: 1 }} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={saveCredentials} disabled={credSaving} style={{ ...S.addBtn, opacity: credSaving ? 0.6 : 1 }}>{credSaving ? "Saving…" : "Save Credentials"}</button>
                  {settings.appConfigured && <button onClick={() => setShowCredForm(false)} style={{ ...S.clearBtn, fontSize: 13 }}>Cancel</button>}
                </div>
              </div>
            ) : (
              <button onClick={() => setShowCredForm(true)} style={{ ...S.clearBtn, fontSize: 13 }}>Update Credentials</button>
            )}
          </div>

          {/* Callback URL reminder */}
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>TikTok App Setup</div>
            <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", lineHeight: 1.6 }}>
              In Partner Center → your app → set this exact <strong>Callback / Redirect URL</strong> and grant the Order, Product, Fulfillment and Finance scopes:
            </p>
            <div style={{ marginTop: 8, padding: "10px 14px", background: "#FFFFFF", border: "2px solid #0A0A0F", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#0033FF", wordBreak: "break-all" }}>
              {API_URL.replace(/\/$/, "")}/api/tiktok/oauth/callback
            </div>
          </div>
        </div>
      )}

      {/* ── Orders Tab (synced sales) ── */}
      {tab === "orders" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders..." style={{ ...S.input, flex: 1, maxWidth: 300 }} />
            <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{filteredOrders.length} orders</div>
            <button onClick={fetchOrders} disabled={loading} style={{ ...S.iconBtn, opacity: loading ? 0.5 : 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
            </button>
          </div>

          {!settings.connected && orders.length === 0 && (
            <div style={{ ...S.lineCard, padding: 24, textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Not connected to TikTok Shop yet</div>
              <button onClick={() => setTab("connect")} style={S.addBtn}>Connect to TikTok Shop</button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "rgba(10,10,15,0.58)" }}>Loading orders...</div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Order #</th>
                    <th style={S.th}>Buyer</th>
                    <th style={S.th}>Item</th>
                    <th style={S.th}>SKU</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Amount</th>
                    <th style={S.th}>Status</th>
                    <th style={{ ...S.th, textAlign: "center" }}>Qty Deducted</th>
                    <th style={S.th}>Date</th>
                    <th style={{ ...S.th, textAlign: "center" }}>Label</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const items = o.items || [];
                    const hasMultiple = items.length > 1;
                    const isExpanded = expandedOrder === o.id;
                    return (
                      <React.Fragment key={o.id}>
                        <tr onClick={() => hasMultiple && setExpandedOrder(isExpanded ? null : o.id)} style={{ cursor: hasMultiple ? "pointer" : "default" }}>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>
                            {hasMultiple && <span style={{ display: "inline-block", width: 16, color: "rgba(10,10,15,0.58)", fontSize: 12 }}>{isExpanded ? "▾" : "▸"}</span>}
                            {o.orderId}
                          </td>
                          <td style={{ ...S.td, fontSize: 14, color: "#0A0A0F" }}>{o.buyerName || "—"}</td>
                          <td style={{ ...S.td, maxWidth: 200 }}>
                            <div style={{ fontSize: 13, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.title || "—"}</div>
                          </td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: o.sku ? "#0A0A0F" : "rgba(10,10,15,0.58)" }}>{o.sku || "—"}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#0033FF" }}>{money(o.amount, o.currency)}</td>
                          <td style={S.td}>{fmtStatus(o.status)}</td>
                          <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", color: o.quantityDeducted > 0 ? "#34d399" : "rgba(10,10,15,0.58)" }}>
                            {o.quantityDeducted > 0 ? `-${o.quantityDeducted}` : "0"}
                          </td>
                          <td style={{ ...S.td, fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{o.syncedAt ? new Date(o.syncedAt).toLocaleString() : "—"}</td>
                          <td style={{ ...S.td, textAlign: "center" }}>
                            {isShippable(o.status) ? (
                              <button onClick={(e) => { e.stopPropagation(); getLabel(o.orderId); }} disabled={labeling === o.orderId} style={{ ...S.iconBtn, fontSize: 12, opacity: labeling === o.orderId ? 0.5 : 1 }} title="Download shipping label">
                                {labeling === o.orderId ? "…" : "⬇ Label"}
                              </button>
                            ) : "—"}
                          </td>
                        </tr>
                        {isExpanded && items.map((item, i) => (
                          <tr key={`${o.id}-${i}`} style={{ background: "#FFFFFF" }}>
                            <td style={{ ...S.td, paddingLeft: 32 }} />
                            <td style={S.td} />
                            <td style={{ ...S.td, maxWidth: 200 }}>
                              <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title || "—"}</div>
                            </td>
                            <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "rgba(10,10,15,0.58)" }}>{item.sku || "—"}</td>
                            <td style={S.td} />
                            <td style={S.td} />
                            <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: item.deducted > 0 ? "#34d399" : "rgba(10,10,15,0.58)" }}>
                              {item.quantity || 1} qty{item.deducted > 0 ? ` (−${item.deducted})` : ""}
                            </td>
                            <td style={S.td} />
                            <td style={S.td} />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {filteredOrders.length === 0 && !loading && (
                    <tr><td colSpan={9} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>
                      {orders.length === 0 ? "No orders yet. Connect to TikTok Shop and orders will sync here automatically." : "No matching orders"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Listings Tab (products) ── */}
      {tab === "listings" && (
        <>
          {!settings.connected ? (
            <div style={{ ...S.lineCard, padding: 24, textAlign: "center" }}>
              <div style={{ fontSize: 16, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Not connected to TikTok Shop</div>
              <button onClick={() => setTab("connect")} style={S.addBtn}>Connect to TikTok Shop</button>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search listings..." style={{ ...S.input, flex: 1, maxWidth: 300 }} />
                <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{filteredListings.length} products</div>
                <button onClick={fetchListings} disabled={loading} style={{ ...S.iconBtn, opacity: loading ? 0.5 : 1 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                  </svg>
                </button>
              </div>
              {loading ? (
                <div style={{ textAlign: "center", padding: 40, color: "rgba(10,10,15,0.58)" }}>Loading listings from TikTok...</div>
              ) : (
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Product</th>
                        <th style={S.th}>Seller SKU</th>
                        <th style={S.th}>Status</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Price</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Stock</th>
                        <th style={{ ...S.th, textAlign: "center" }}>Save</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredListings.flatMap((l) => (l.skus && l.skus.length ? l.skus : [{ id: l.id, sellerSku: "", price: "", stock: "" }]).map((sk, i) => {
                        const e = edits[sk.id] || {};
                        const dirty = e.price !== undefined || e.stock !== undefined;
                        return (
                          <tr key={`${l.id}-${sk.id}-${i}`}>
                            <td style={{ ...S.td, maxWidth: 280 }}>
                              {i === 0 && <div style={{ fontSize: 13, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.title || "—"}</div>}
                            </td>
                            <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: sk.sellerSku ? "#0A0A0F" : "rgba(10,10,15,0.58)" }}>{sk.sellerSku || "—"}</td>
                            <td style={S.td}>{i === 0 ? <span style={{ ...S.typeBadge, fontSize: 12, color: l.status === "ACTIVATE" || l.status === "LIVE" ? "#34d399" : "#0A0A0F" }}>{l.status || "—"}</span> : ""}</td>
                            <td style={{ ...S.td, textAlign: "right" }}>
                              <input
                                value={e.price !== undefined ? e.price : (sk.price ?? "")}
                                onChange={(ev) => setEdits((prev) => ({ ...prev, [sk.id]: { ...prev[sk.id], price: ev.target.value } }))}
                                style={{ ...S.input, width: 80, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", padding: "4px 6px" }}
                              />
                            </td>
                            <td style={{ ...S.td, textAlign: "right" }}>
                              <input
                                value={e.stock !== undefined ? e.stock : (sk.stock ?? "")}
                                onChange={(ev) => setEdits((prev) => ({ ...prev, [sk.id]: { ...prev[sk.id], stock: ev.target.value } }))}
                                style={{ ...S.input, width: 70, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", padding: "4px 6px" }}
                              />
                            </td>
                            <td style={{ ...S.td, textAlign: "center" }}>
                              <button onClick={() => saveSku(l.id, sk.id)} disabled={!dirty || savingSku === sk.id} style={{ ...S.iconBtn, fontSize: 12, opacity: (!dirty || savingSku === sk.id) ? 0.4 : 1 }}>
                                {savingSku === sk.id ? "…" : "Push"}
                              </button>
                            </td>
                          </tr>
                        );
                      }))}
                      {filteredListings.length === 0 && !loading && (
                        <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>No listings found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Payouts Tab (finance / settlements) ── */}
      {tab === "payouts" && (
        <>
          {!settings.connected ? (
            <div style={{ ...S.lineCard, padding: 24, textAlign: "center" }}>
              <div style={{ fontSize: 16, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Not connected to TikTok Shop</div>
              <button onClick={() => setTab("connect")} style={S.addBtn}>Connect to TikTok Shop</button>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
                <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{payouts.length} statements</div>
                <button onClick={fetchPayouts} disabled={loading} style={{ ...S.iconBtn, opacity: loading ? 0.5 : 1 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                  </svg>
                </button>
              </div>
              {loading ? (
                <div style={{ textAlign: "center", padding: 40, color: "rgba(10,10,15,0.58)" }}>Loading payouts from TikTok...</div>
              ) : (
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Statement</th>
                        <th style={S.th}>Period</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Revenue</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Fees</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Net Payout</th>
                        <th style={S.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((p) => (
                        <tr key={p.id || p.statementId}>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{p.statementId || "—"}</td>
                          <td style={{ ...S.td, fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{p.statementTime ? new Date(Number(p.statementTime) * 1000).toLocaleDateString() : "—"}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{money(p.revenueAmount, p.currency)}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#ef4444" }}>{p.feeAmount ? `-${money(Math.abs(p.feeAmount), p.currency)}` : "—"}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#34d399", fontWeight: 600 }}>{money(p.settlementAmount, p.currency)}</td>
                          <td style={S.td}><span style={{ ...S.typeBadge, fontSize: 12, color: p.status === "PAID" ? "#34d399" : "#0033FF" }}>{p.status || "—"}</span></td>
                        </tr>
                      ))}
                      {payouts.length === 0 && !loading && (
                        <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>No settlement statements yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
